import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getUserRole } from "@/lib/roles";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrokerSidebar } from "@/components/broker/BrokerSidebar";
import { BrokerDashboard } from "@/components/broker/BrokerDashboard";
import { BrokerListings } from "@/components/broker/BrokerListings";
import { BrokerLeads } from "@/components/broker/BrokerLeads";
import { BrokerReports } from "@/components/broker/BrokerReports";
import { AIBusinessMatching } from "@/components/broker/AIBusinessMatching";
import { AIBusinessValuation } from "@/components/broker/AIBusinessValuation";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useSearchParams } from "react-router-dom";
import { LayoutDashboard, FileText, Users, BarChart3, Brain, Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";



const menuItems = [
  { id: "dashboard", title: "Dashboard", icon: LayoutDashboard },
  { id: "listings", title: "Listings", icon: FileText },
  { id: "leads", title: "Leads", icon: Users },
  { id: "reports", title: "Reports", icon: BarChart3 },
  { id: "ai-matching", title: "AI Business Matching", icon: Brain },
  { id: "ai-valuation", title: "AI Valuation", icon: Calculator },
];

export default function BrokerPortal() {
  const navigate = useNavigate();
  const { user, isAuthReady } = useAuth();
  const { toast } = useToast();
  const [ready, setReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("dashboard");
  
  usePageTitle("Broker Portal");

  // Add portal-page class for CSS targeting
  useEffect(() => {
    document.documentElement.classList.add("portal-page");
    return () => document.documentElement.classList.remove("portal-page");
  }, []);

  // Single auth/role guard effect
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!isAuthReady) return; // wait for auth subsystem to init

      // not logged in → go sign in (single redirect path)
      if (!user) {
        navigate('/sign-in?redirect=/broker-portal', { replace: true });
        return;
      }

      // logged in → fetch role once
      try {
        const role = await getUserRole(user.id);
        if (cancelled) return;

        if (role !== 'broker') {
          // sellers/admins go elsewhere
          navigate('/dashboard', { replace: true });
          return;
        }

        // OK to render portal
        setReady(true);
      } catch (e) {
        // fail-safe: never white screen
        console.error('role check failed', e);
        navigate('/dashboard', { replace: true });
      }
    }

    run();
    return () => { cancelled = true; };
  }, [isAuthReady, user, navigate]);

  // Handle payment verification on return from Stripe
  useEffect(() => {
    const handlePaymentVerification = async () => {
      const paymentStatus = searchParams.get('payment');
      const sessionId = searchParams.get('session_id');
      const listingId = searchParams.get('listing_id');

      if (paymentStatus === 'success' && sessionId && listingId && user) {
        console.log('[Payment Verification] Starting verification:', { sessionId, listingId });
        
        try {
          const { data, error } = await supabase.functions.invoke('verify-listing-payment', {
            body: { session_id: sessionId, listing_id: listingId },
          });

          if (error) {
            console.error('[Payment Verification] Error:', error);
            toast({
              title: "Payment Verification Failed",
              description: error.message || "Could not verify payment. Please contact support.",
              variant: "destructive",
            });
          } else {
            console.log('[Payment Verification] Success:', data);
            toast({
              title: "Payment Successful!",
              description: "Your listing has been activated and is now live.",
            });
            // Switch to listings view to show the updated listing
            setActiveSection('listings');
          }
        } catch (err: any) {
          console.error('[Payment Verification] Exception:', err);
          toast({
            title: "Verification Error",
            description: err.message || "An unexpected error occurred.",
            variant: "destructive",
          });
        }

        // Clean up URL parameters
        searchParams.delete('payment');
        searchParams.delete('session_id');
        searchParams.delete('listing_id');
        setSearchParams(searchParams, { replace: true });
      }
    };

    if (ready && user) {
      handlePaymentVerification();
    }
  }, [ready, user, searchParams, setSearchParams, toast]);

  // Allow deep-linking to a specific section (e.g., ?section=listings)
  useEffect(() => {
    const section = searchParams.get('section');
    if (section) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // never navigate here; only render a loader until ready
  if (!isAuthReady || !ready) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="text-center">Loading broker portal…</div>
        </main>
        <Footer />
      </div>
    );
  }

  // Render the broker portal content sections
  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <BrokerDashboard />;
      case "listings":
        return <BrokerListings />;
      case "leads":
        return <BrokerLeads />;
      case "reports":
        return <BrokerReports />;
      case "ai-matching":
        return <AIBusinessMatching />;
      case "ai-valuation":
        return <AIBusinessValuation />;
      default:
        return <BrokerDashboard />;
    }
  };

  return (
    <div data-portal-root>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div id="broker-portal-topbar" className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10 p-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="-ml-1" />
            <h1 className="text-xl font-semibold">
              {menuItems.find(item => item.id === activeSection)?.title || 'Dashboard'}
            </h1>
          </div>
        </div>
        <div className="flex flex-1 w-full">
          <BrokerSidebar
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
          <div className="flex-1 flex flex-col w-full" data-portal-content data-sidebar="content" style={{ paddingTop: '176px' }}>
            <main className="flex-1 p-4 md:p-6">
              {renderContent()}
            </main>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
